import { AnswerStatus } from '@/types/answer/AnswerStatus';
import { Box, Container, Grid, useMediaQuery } from '@mui/material';
import { CookieConsentBanner } from '@/components/common/CookieConsentBanner';
import { Footer } from '@/components/main/footer/Footer';
import { Header } from '@/components/common/Header';
import { LetterSimpleStatus } from '@/types/letter/LetterSimpleStatus';
import { MainNavigation } from '@/components/main/navigation/MainNavigation';
import { MobileHeader } from '@/components/common/MobileHeader';
import { Outlet } from 'react-router-dom';
import { PhoneBindingGate } from '@/components/common/phone/PhoneBindingGate';
import { Theme } from '@mui/material/styles';
import { TopLine } from '@/components/common/TopLine';
import { UserDTO } from '@/types/user/UserDTO';
import { getAuthenticated, getCurrentUser } from '@/services/authSlice';
import { useAppSelector } from '@/app/hooks';
import { useCountLetterDraftsQuery } from '@/services/api/letterDraftApiSlice';
import { useGetLettersCountQuery } from '@/services/api/letterApiSlice';
import { useGetSendersQuery } from '@/services/api/createFormApiSlice';
import React, { FC, Fragment, ReactElement } from 'react';

export const Main: FC = (): ReactElement => {
  const isAuthenticated: boolean = useAppSelector(getAuthenticated);
  const authUser: UserDTO | undefined = useAppSelector(getCurrentUser);

  const { data: inboxCount } = useGetLettersCountQuery(
    {
      status: LetterSimpleStatus.ALL,
      answerStatus: AnswerStatus.PROCESSED,
      userId: authUser?.id,
    },
    { skip: !authUser },
  );
  const { data: outboxCount } = useGetLettersCountQuery(
    {
      status: LetterSimpleStatus.ALL,
      userId: authUser?.id,
    },
    { skip: !authUser },
  );
  const { data: senders, isLoading: isLoadingSenders } = useGetSendersQuery(authUser?.id, { skip: !authUser });
  const { data: letterDraftsCount } = useCountLetterDraftsQuery(authUser?.id, {
    skip: !authUser,
  });

  const isMobile: boolean = useMediaQuery((theme: Theme): string => theme.breakpoints.down('sm'));

  return (
    <Fragment>
      <TopLine />
      <Container
        sx={{
          display: 'flex',
          flexDirection: 'column',
          marginTop: '-6px',
          minHeight: '100%',
        }}>
        <Box display={'flex'} flex={1}>
          <Grid container={true} direction={'column'} wrap={'nowrap'} spacing={isMobile ? 5 : 7}>
            <Grid item={true} pb={(theme: Theme) => (isMobile ? theme.spacing(1.5) : 'inherit')}>
              {isMobile ? (
                <MobileHeader inboxCount={inboxCount} outboxCount={outboxCount} letterDraftsCount={letterDraftsCount} />
              ) : authUser ? (
                <Header profile={!!authUser} />
              ) : (
                <MainNavigation profile={!!authUser} isAuthenticated={isAuthenticated} inboxCount={inboxCount} />
              )}
            </Grid>
            <Grid item={true} xs={true}>
              <Outlet context={{ inboxCount, outboxCount, senders, letterDraftsCount, isLoadingSenders }} />
            </Grid>
            {isMobile ? null : (
              <Grid item={true}>
                <Footer isMobile={isMobile} />
              </Grid>
            )}
          </Grid>
        </Box>
        <CookieConsentBanner />
        {/* Only the reminder: the barrier is mounted above the whole route tree in `Root`. */}
        <PhoneBindingGate handles={'RECOMMENDED'} />
      </Container>
    </Fragment>
  );
};
